'use client'

import { type RefObject, useEffect, useRef } from 'react'

interface IUseTrackOnVisibleArgs {
  ref: RefObject<HTMLElement | null>
  /** 노출 시 1회 호출되는 트래커 (carStoryTracking.show* 메서드) */
  tracker: () => void
  enabled?: boolean
  threshold?: number
}

/**
 * IntersectionObserver로 ref가 처음 화면에 들어오면 tracker를 1회 호출하고 disconnect.
 * 같은 ref가 다시 보여도 재호출 안 함.
 */
const useTrackOnVisible = ({
  ref,
  tracker,
  enabled = true,
  threshold = 0.1,
}: IUseTrackOnVisibleArgs) => {
  const loggedRef = useRef(false)
  // tracker는 호출부에서 매 렌더마다 새 함수 참조가 만들어지는 경우가 많아 ref로 안정화
  const trackerRef = useRef(tracker)
  trackerRef.current = tracker

  useEffect(
    function observeFirstVisibility() {
      if (!enabled || loggedRef.current) return
      const node = ref.current
      if (!node) return
      if (typeof IntersectionObserver === 'undefined') return

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting || loggedRef.current) continue
            loggedRef.current = true
            trackerRef.current()
            observer.disconnect()
          }
        },
        { threshold }
      )
      observer.observe(node)
      return () => observer.disconnect()
    },
    [ref, enabled, threshold]
  )
}

export default useTrackOnVisible
